import { EFinancingProgram } from '@if/core/types'
import { EasyCareWorksheet, EditEasyCareWorksheetDto } from '@src/data/types/EasyCareWorksheetSchema'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const worksheetUrl = '/Applications/easycare/{creditApplicationId}/Worksheet'

const buildUrl = (creditApplicationId: string) => worksheetUrl.replace('{creditApplicationId}', creditApplicationId)

export async function createEasyCareWorksheet(data: EditEasyCareWorksheetDto): Promise<EasyCareWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.post<EasyCareWorksheet>(buildUrl(data.creditApplicationId), data)
  return response.data
}

export async function updateEasyCareWorksheet(data: EditEasyCareWorksheetDto): Promise<EasyCareWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.put<EasyCareWorksheet>(buildUrl(data.creditApplicationId), data)
  return response.data
}

function useSaveEasyCareWorksheet(
  mutationFn: (data: EditEasyCareWorksheetDto) => Promise<EasyCareWorksheet>,
): [MutationFunction<EasyCareWorksheet, EditEasyCareWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn,
    onSuccess: (savedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(savedWorksheet.creditApplicationId, EFinancingProgram.EasyCareProtectionPlan)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: savedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useCreateEasyCareWorksheet(): [MutationFunction<EasyCareWorksheet, EditEasyCareWorksheetDto>, boolean] {
  return useSaveEasyCareWorksheet(createEasyCareWorksheet)
}

export function useUpdateEasyCareWorksheet(): [MutationFunction<EasyCareWorksheet, EditEasyCareWorksheetDto>, boolean] {
  return useSaveEasyCareWorksheet(updateEasyCareWorksheet)
}
